import { useEffect, useRef, useState } from "react";
import { ChevronDown, X } from "../icons";
import { SORT_OPTIONS, type SortOption } from "./filterUtils";
import InStockSwitch from "./InStockSwitch";
import type useCatalogFilters from "./useCatalogFilters";
import { useLanguage, type TranslationKey } from "../../context/LanguageContext";

type Filters = ReturnType<typeof useCatalogFilters>;

const SORT_LABEL_KEYS: Record<SortOption, TranslationKey> = {
  relevance: "sortRelevance",
  price_asc: "sortPriceAsc",
  price_desc: "sortPriceDesc",
  name_asc: "sortNameAsc",
  name_desc: "sortNameDesc",
  newest: "sortNewest",
};

// Plain useState + a document-level click listener rather than native
// <details>/<summary> — <details> doesn't close on an outside click on
// its own, which a dropdown menu needs to feel right.
function SortDropdown({ value, onChange }: { value: SortOption; onChange: (v: SortOption) => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs text-steel-600 transition-colors duration-200 ease-engineered hover:text-ink sm:text-sm"
      >
        <span className="hidden text-steel-400 sm:inline">{t("sortLabel")}:</span>
        <span className="text-ink">{t(SORT_LABEL_KEYS[value])}</span>
        <ChevronDown size={14} className={`text-steel-400 transition-transform duration-200 ease-engineered ${open ? "rotate-180" : ""}`} />
      </button>
      {/* Always mounted (not `{open && ...}`) so closing can transition
          out instead of vanishing instantly — pointer-events/visibility
          keep it inert and invisible to a11y tools while closed. */}
      <div
        aria-hidden={!open}
        className={`absolute right-0 top-full z-20 mt-2 w-64 origin-top-right divide-y divide-steel-100 border border-steel-200 bg-white shadow-lg transition-[opacity,transform] duration-150 ease-engineered ${
          open ? "opacity-100" : "invisible scale-95 opacity-0"
        }`}
      >
        {SORT_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            tabIndex={open ? 0 : -1}
            onClick={() => {
              onChange(option);
              setOpen(false);
            }}
            className={`block w-full px-4 py-2.5 text-left text-sm transition-colors duration-150 ease-engineered hover:bg-steel-50 ${
              option === value ? "text-accent" : "text-ink"
            }`}
          >
            {t(SORT_LABEL_KEYS[option])}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CatalogToolbar({ filters }: { filters: Filters }) {
  const { t } = useLanguage();
  const {
    inStockOnly,
    setInStockOnly,
    inStockCount,
    sortBy,
    setSortBy,
    selectedBrands,
    toggleBrand,
    numericFilters,
    setNumericFilter,
    selectFilters,
    toggleSelectOption,
    specFieldDefs,
    activeFilterCount,
    clearAll,
    displayed,
  } = filters;

  // No "in stock" chip here — the switch above is already its own visible
  // on/off state and its own removal control, so a second removable chip
  // for the same thing was pure repetition.
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  for (const brand of selectedBrands) {
    chips.push({ key: `brand-${brand}`, label: brand, onRemove: () => toggleBrand(brand) });
  }
  for (const field of specFieldDefs) {
    const range = numericFilters[field.key];
    if (range) {
      const label =
        range.min !== undefined && range.max !== undefined
          ? `${field.label}: ${range.min}–${range.max}`
          : range.min !== undefined
            ? `${field.label}: ${t("rangeFrom")} ${range.min}`
            : `${field.label}: ${t("rangeTo")} ${range.max}`;
      chips.push({ key: `num-${field.key}`, label, onRemove: () => setNumericFilter(field.key, undefined) });
    }
    const selected = selectFilters[field.key];
    if (selected) {
      for (const option of selected) {
        chips.push({
          key: `sel-${field.key}-${option}`,
          label: option,
          onRemove: () => toggleSelectOption(field.key, option),
        });
      }
    }
  }

  return (
    <div className="mb-3 flex flex-col gap-3">
      {/* One row at every width now — the switch that used to compete for
          space here on mobile moved into the filter drawer (see
          CatalogSidebar), so counter + sort alone comfortably fit a single
          row even on narrow phones. The switch still renders here, but
          only at lg+. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <span className="hidden lg:flex">
            <InStockSwitch checked={inStockOnly} count={inStockCount} onChange={setInStockOnly} />
          </span>
          <p className="text-xs text-ink sm:text-sm">
            {displayed.length} {t("productCountSuffix")}
          </p>
        </div>

        <SortDropdown value={sortBy} onChange={setSortBy} />
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.onRemove}
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap border border-steel-300 px-2.5 py-1 text-xs text-steel-600 hover:border-accent hover:text-accent"
            >
              {chip.label}
              <X size={12} />
            </button>
          ))}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1.5 border border-accent/40 px-2.5 py-1 text-xs font-medium text-accent transition-colors duration-200 ease-engineered hover:border-accent hover:bg-accent hover:text-white"
            >
              <X size={12} />
              {t("clearAllFilters")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
